import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaDownload,
  FaImage,
  FaMagic,
  FaSearch,
  FaSpinner,
  FaUpload,
} from 'react-icons/fa';
import { useUser } from '../../../../UserContext';
import './BannerCreator.css';

const OUTPUT_WIDTH = 171;
const OUTPUT_HEIGHT = 349;
const RENDER_SCALE = 3;
const RENDER_WIDTH = OUTPUT_WIDTH * RENDER_SCALE;
const RENDER_HEIGHT = OUTPUT_HEIGHT * RENDER_SCALE;
const BANNER_TEMPLATE_ZIP = '/images/banners/creator/banners.zip';
const MANA_GEM_GUIDE_SRC = '/assets/Card_Inhand_BannerAtlas_Minion_ManaGem.webp';
const MANA_GEM_GUIDE_X = -7;
const MANA_GEM_GUIDE_Y = -14;

/*
 * Each base has a complete image and a same-size isolated cloth layer.
 * The editor always exports one flattened 171x349 PNG.
 */
const BANNER_BASES = [
  {
    id: 'tradeable',
    label: 'Tradeable',
    src: '/images/banners/creator/banner_tradeable_noemblem.png',
    isolatedSrc: '/images/banners/creator/banner_tradeable_noemblem_isolated.png',
    iconX: 76,
    iconY: 228,
    iconMaxWidth: 95,
    iconMaxHeight: 100,
  },
  {
    id: 'forge',
    label: 'Forge',
    src: '/images/banners/creator/banner_forge.png',
    isolatedSrc: '/images/banners/creator/banner_forge_isolated.png',
    iconX: 76,
    iconY: 228,
    iconMaxWidth: 95,
    iconMaxHeight: 100,
  },
  {
    id: 'mercs',
    label: 'Mercenaries',
    src: '/images/banners/creator/banner_mercs_noemblem.png',
    isolatedSrc: '/images/banners/creator/banner_mercs_noemblem_isolated.png',
    iconX: 78,
    iconY: 230,
    iconMaxWidth: 95,
    iconMaxHeight: 100,
  },
  {
    id: 'season1',
    label: 'Banner',
    src: '/images/banners/creator/banner_s1_noemblem.png',
    isolatedSrc: '/images/banners/creator/banner_s1_noemblem_isolated.png',
    iconX: 78,
    iconY: 230,
    iconMaxWidth: 95,
    iconMaxHeight: 100,
  },
  {
    id: 'season2',
    label: 'Banner 2',
    src: '/images/banners/creator/banner_custom2.png',
    isolatedSrc: '/images/banners/creator/banner_custom2_isolated.png',
    iconX: 78,
    iconY: 230,
    iconMaxWidth: 95,
    iconMaxHeight: 100,
  },
];

const ICON_STYLES = [
  { id: 'original', label: 'Image' },
  { id: 'gold', label: 'Gold Emblem' },
  { id: 'silver', label: 'Silver Emblem' },
  { id: 'white', label: 'White Emblem' },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function getFittedIconScale(artwork, base) {
  if (!artwork) return 1;
  return Math.min(
    (base?.iconMaxWidth ?? 95) / artwork.width,
    (base?.iconMaxHeight ?? 100) / artwork.height,
  );
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((value) => clamp(value, 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

function imageFromUrl(src, crossOrigin = false) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load image: ${src}`));
    image.src = src;
  });
}

function imageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be decoded as an image.'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not create the PNG.'))),
      'image/png',
    );
  });
}

function makeOutputCanvas(previewCanvas, finishedImage = null) {
  const output = document.createElement('canvas');
  output.width = OUTPUT_WIDTH;
  output.height = OUTPUT_HEIGHT;
  const ctx = output.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    finishedImage || previewCanvas,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT,
  );
  return output;
}

function makeTintedAlpha(source, fill) {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return canvas;
}

function exactDistance1D(source) {
  const length = source.length;
  const output = new Float64Array(length);
  const locations = new Int32Array(length);
  const boundaries = new Float64Array(length + 1);
  let envelope = 0;
  locations[0] = 0;
  boundaries[0] = -Infinity;
  boundaries[1] = Infinity;

  for (let q = 1; q < length; q += 1) {
    let intersection =
      ((source[q] + q * q) -
        (source[locations[envelope]] +
          locations[envelope] * locations[envelope])) /
      (2 * q - 2 * locations[envelope]);
    while (intersection <= boundaries[envelope]) {
      envelope -= 1;
      intersection =
        ((source[q] + q * q) -
          (source[locations[envelope]] +
            locations[envelope] * locations[envelope])) /
        (2 * q - 2 * locations[envelope]);
    }
    envelope += 1;
    locations[envelope] = q;
    boundaries[envelope] = intersection;
    boundaries[envelope + 1] = Infinity;
  }

  envelope = 0;
  for (let q = 0; q < length; q += 1) {
    while (boundaries[envelope + 1] < q) envelope += 1;
    const delta = q - locations[envelope];
    output[q] = delta * delta + source[locations[envelope]];
  }
  return output;
}

/*
 * Exact squared-Euclidean distance transform (Felzenszwalb/Huttenlocher).
 * A one-pixel transparent border prevents full-bleed masks from losing their
 * outside boundary.
 */
function exactEuclideanDistance(mask, width, height, zeroForInside) {
  const paddedWidth = width + 2;
  const paddedHeight = height + 2;
  const infinity = 1e12;
  const field = new Float64Array(paddedWidth * paddedHeight);

  for (let y = 0; y < paddedHeight; y += 1) {
    for (let x = 0; x < paddedWidth; x += 1) {
      const isPadding =
        x === 0 || y === 0 || x === paddedWidth - 1 || y === paddedHeight - 1;
      const isInside = isPadding ? false : mask[(y - 1) * width + (x - 1)] === 1;
      field[y * paddedWidth + x] = isInside === zeroForInside ? 0 : infinity;
    }
  }

  const vertical = new Float64Array(field.length);
  for (let x = 0; x < paddedWidth; x += 1) {
    const column = new Float64Array(paddedHeight);
    for (let y = 0; y < paddedHeight; y += 1) {
      column[y] = field[y * paddedWidth + x];
    }
    const transformed = exactDistance1D(column);
    for (let y = 0; y < paddedHeight; y += 1) {
      vertical[y * paddedWidth + x] = transformed[y];
    }
  }

  const squared = new Float64Array(field.length);
  for (let y = 0; y < paddedHeight; y += 1) {
    const row = vertical.subarray(y * paddedWidth, (y + 1) * paddedWidth);
    squared.set(exactDistance1D(row), y * paddedWidth);
  }

  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      result[y * width + x] = Math.sqrt(
        squared[(y + 1) * paddedWidth + (x + 1)],
      );
    }
  }
  return result;
}

function boxBlurFloatInteger(values, width, height, radius) {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) return values;
  const horizontal = new Float32Array(values.length);
  const output = new Float32Array(values.length);

  for (let y = 0; y < height; y += 1) {
    let sum = 0;
    for (let x = -r; x <= r; x += 1) {
      sum += values[y * width + clamp(x, 0, width - 1)];
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[y * width + x] = sum / (r * 2 + 1);
      const leaving = clamp(x - r, 0, width - 1);
      const entering = clamp(x + r + 1, 0, width - 1);
      sum += values[y * width + entering] - values[y * width + leaving];
    }
  }

  for (let x = 0; x < width; x += 1) {
    let sum = 0;
    for (let y = -r; y <= r; y += 1) {
      sum += horizontal[clamp(y, 0, height - 1) * width + x];
    }
    for (let y = 0; y < height; y += 1) {
      output[y * width + x] = sum / (r * 2 + 1);
      const leaving = clamp(y - r, 0, height - 1);
      const entering = clamp(y + r + 1, 0, height - 1);
      sum +=
        horizontal[entering * width + x] -
        horizontal[leaving * width + x];
    }
  }
  return output;
}

function boxBlurFloat(values, width, height, radius) {
  const continuousRadius = Math.max(0, radius);
  const lowerRadius = Math.floor(continuousRadius);
  const mix = continuousRadius - lowerRadius;
  const lower = boxBlurFloatInteger(values, width, height, lowerRadius);
  if (mix <= 0.0001) return lower;

  const upper = boxBlurFloatInteger(values, width, height, lowerRadius + 1);
  const output = new Float32Array(values.length);
  for (let i = 0; i < output.length; i += 1) {
    output[i] = lower[i] + (upper[i] - lower[i]) * mix;
  }
  return output;
}

function blurHeightMap(values, width, height, radius) {
  if (radius <= 0.05) return values;
  const passRadius = radius * 0.58;
  let output = values;
  for (let pass = 0; pass < 3; pass += 1) {
    output = boxBlurFloat(output, width, height, passRadius);
  }
  return output;
}

/*
 * A compact distance-field approximation of Photoshop's Inner Bevel.
 * The effect is calculated at up to 512px, then scaled with the icon.
 */
function makeBevelEffect(source, options) {
  const maxSide = 512;
  const mapScale = Math.min(1, maxSide / Math.max(source.width, source.height));
  // Bevel dimensions live in icon space so the complete treatment scales
  // proportionally when the user changes the icon size.
  const logicalToMap = mapScale;
  const width = Math.max(3, Math.round(source.width * mapScale));
  const height = Math.max(3, Math.round(source.height * mapScale));
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
  maskCtx.imageSmoothingEnabled = true;
  maskCtx.imageSmoothingQuality = 'high';
  maskCtx.drawImage(source, 0, 0, width, height);
  const maskPixels = maskCtx.getImageData(0, 0, width, height).data;
  const mask = new Uint8Array(width * height);
  const maskAlpha = new Float32Array(width * height);
  for (let i = 0; i < mask.length; i += 1) {
    maskAlpha[i] = maskPixels[i * 4 + 3] / 255;
    mask[i] = maskAlpha[i] > 0.03 ? 1 : 0;
  }

  const size = Math.max(0.75, options.size * logicalToMap);
  const softness = Math.max(0, options.softness * logicalToMap);
  let heightMap = new Float32Array(mask.length);
  const coverage = new Float32Array(mask.length);

  for (let i = 0; i < mask.length; i += 1) {
    coverage[i] = maskAlpha[i];
  }

  if (options.technique === 'smooth') {
    /*
     * Smooth is a local bump made from the matte itself. Keeping the built-in
     * radius well below Size prevents opposite edges from merging into a dome,
     * while avoiding the nearest-edge seams that Chisel techniques retain.
     */
    heightMap = blurHeightMap(
      maskAlpha,
      width,
      height,
      Math.max(0.75, size * 0.45 + softness),
    );
  } else {
    const insideDistance = exactEuclideanDistance(mask, width, height, false);
    for (let i = 0; i < mask.length; i += 1) {
      if (mask[i] === 0) continue;
      heightMap[i] = clamp(insideDistance[i] / size, 0, 1);
    }

    let blurRadius = softness;
    if (options.technique === 'chiselSoft') {
      blurRadius += Math.max(0.35, size * 0.04);
    }
    if (options.technique === 'chiselHard') blurRadius *= 0.18;
    heightMap = blurHeightMap(heightMap, width, height, blurRadius);
  }

  let gradientXMap = new Float32Array(mask.length);
  let gradientYMap = new Float32Array(mask.length);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      // Scharr derivatives preserve the shape better than blurring the height
      // profile itself, while reducing one-pixel directional changes.
      gradientXMap[i] =
        (3 * (heightMap[i - width + 1] - heightMap[i - width - 1]) +
          10 * (heightMap[i + 1] - heightMap[i - 1]) +
          3 * (heightMap[i + width + 1] - heightMap[i + width - 1])) /
        32;
      gradientYMap[i] =
        (3 * (heightMap[i + width - 1] - heightMap[i - width - 1]) +
          10 * (heightMap[i + width] - heightMap[i - width]) +
          3 * (heightMap[i + width + 1] - heightMap[i - width + 1])) /
        32;
    }
  }

  const highlight = document.createElement('canvas');
  const shadow = document.createElement('canvas');
  highlight.width = shadow.width = width;
  highlight.height = shadow.height = height;
  const highlightCtx = highlight.getContext('2d');
  const shadowCtx = shadow.getContext('2d');
  const highlightData = highlightCtx.createImageData(width, height);
  const shadowData = shadowCtx.createImageData(width, height);
  const angle = (options.angle * Math.PI) / 180;
  const altitude = (options.altitude * Math.PI) / 180;
  const lightX = Math.cos(angle) * Math.cos(altitude);
  const lightY = -Math.sin(angle) * Math.cos(altitude);
  const lightZ = Math.sin(altitude);
  const direction = options.direction === 'down' ? -1 : 1;
  // Preserve the original response through 300%, then let greater values push
  // the lighting contrast beyond the normal-vector saturation point.
  const depthScale =
    Math.log2(1 + options.depth / 100) * 1.15 * direction;
  const depthLightingGain =
    1 + Math.max(0, options.depth - 300) / 350;
  const textureAmount = clamp(options.texture || 0, 0, 1);

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      if (coverage[i] === 0) continue;
      const gradientX = gradientXMap[i];
      const gradientY = gradientYMap[i];
      let normalX = -gradientX * size * depthScale;
      let normalY = -gradientY * size * depthScale;
      let normalZ = 1;
      const normalLength = Math.hypot(normalX, normalY, normalZ) || 1;
      normalX /= normalLength;
      normalY /= normalLength;
      normalZ /= normalLength;

      const surfaceLight = normalX * lightX + normalY * lightY + normalZ * lightZ;
      const flatLight = lightZ;
      const lighting =
        (surfaceLight - flatLight) * 1.35 * depthLightingGain;
      const pixel = i * 4;
      if (lighting >= 0) {
        const strength =
          clamp(Math.pow(lighting, 0.82) * coverage[i], 0, 1) *
          options.highlightOpacity;
        highlightData.data[pixel] = 255;
        highlightData.data[pixel + 1] = 255;
        highlightData.data[pixel + 2] = 255;
        highlightData.data[pixel + 3] = Math.round(strength * 255);
      } else {
        let strength =
          clamp(Math.pow(-lighting, 0.82) * coverage[i], 0, 1) *
          options.shadowOpacity;
        if (textureAmount > 0) {
          // Fine deterministic grain affects only the bevel's darkened pixels.
          const hash = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
          const grain = hash - Math.floor(hash);
          strength = clamp(
            strength * (1 + (grain - 0.5) * textureAmount * 0.7),
            0,
            1,
          );
        }
        shadowData.data[pixel] = 12;
        shadowData.data[pixel + 1] = 7;
        shadowData.data[pixel + 2] = 5;
        shadowData.data[pixel + 3] = Math.round(strength * 255);
      }
    }
  }
  highlightCtx.putImageData(highlightData, 0, 0);
  shadowCtx.putImageData(shadowData, 0, 0);
  return { highlight, shadow };
}

function styleIcon(
  source,
  style,
  brightness,
  contrast,
  saturation,
  sourceDetail,
) {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d');

  ctx.filter =
    style === 'original'
      ? `brightness(${brightness})`
      : `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`;
  ctx.drawImage(source, 0, 0);
  ctx.filter = 'none';

  if (style === 'original') return canvas;

  const palette = {
    gold: ['#fff4a8', '#e5ad2d', '#6f3308'],
    silver: ['#ffffff', '#bfc5d2', '#3d4657'],
    white: ['#ffffff', '#e5edf7', '#8693a5'],
  }[style];

  const colorLayer = document.createElement('canvas');
  colorLayer.width = canvas.width;
  colorLayer.height = canvas.height;
  const colorCtx = colorLayer.getContext('2d');
  // Start with alpha only. Non-original styles do not inherit source artwork.
  colorCtx.drawImage(makeTintedAlpha(source, '#ffffff'), 0, 0);
  colorCtx.globalCompositeOperation = 'source-in';
  const gradient = colorCtx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, palette[0]);
  gradient.addColorStop(0.34, palette[1]);
  gradient.addColorStop(0.68, palette[1]);
  gradient.addColorStop(1, palette[2]);
  colorCtx.fillStyle = gradient;
  colorCtx.fillRect(0, 0, canvas.width, canvas.height);

  // Broad uneven darkening gives the material a forged/painted surface.
  colorCtx.globalCompositeOperation = 'multiply';
  colorCtx.globalAlpha = 0.32;
  const shade = colorCtx.createRadialGradient(
    canvas.width * 0.32,
    canvas.height * 0.22,
    0,
    canvas.width * 0.58,
    canvas.height * 0.54,
    Math.max(canvas.width, canvas.height) * 0.72,
  );
  shade.addColorStop(0, 'rgba(255,255,255,1)');
  shade.addColorStop(0.46, 'rgba(224,205,170,0.92)');
  shade.addColorStop(1, 'rgba(48,27,18,0.72)');
  colorCtx.fillStyle = shade;
  colorCtx.fillRect(0, 0, canvas.width, canvas.height);

  // Optional source visibility. Zero means a pure alpha silhouette.
  if (sourceDetail > 0) {
    colorCtx.globalCompositeOperation = 'source-over';
    colorCtx.globalAlpha = sourceDetail;
    colorCtx.drawImage(source, 0, 0);
  }

  // Reapply the exact source alpha after all full-canvas material passes.
  colorCtx.globalCompositeOperation = 'destination-in';
  colorCtx.globalAlpha = 1;
  colorCtx.drawImage(source, 0, 0);
  colorCtx.globalCompositeOperation = 'source-over';

  const finishedLayer = document.createElement('canvas');
  finishedLayer.width = canvas.width;
  finishedLayer.height = canvas.height;
  const finishedCtx = finishedLayer.getContext('2d');
  finishedCtx.filter = `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`;
  finishedCtx.drawImage(colorLayer, 0, 0);
  return finishedLayer;
}

function cropToAlphaBounds(canvas) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let left = canvas.width;
  let top = canvas.height;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      if (pixels[(y * canvas.width + x) * 4 + 3] === 0) continue;
      left = Math.min(left, x);
      top = Math.min(top, y);
      right = Math.max(right, x);
      bottom = Math.max(bottom, y);
    }
  }

  if (right < left || bottom < top) return canvas;
  if (
    left === 0 &&
    top === 0 &&
    right === canvas.width - 1 &&
    bottom === canvas.height - 1
  ) {
    return canvas;
  }

  const cropped = document.createElement('canvas');
  cropped.width = right - left + 1;
  cropped.height = bottom - top + 1;
  cropped
    .getContext('2d')
    .drawImage(
      canvas,
      left,
      top,
      cropped.width,
      cropped.height,
      0,
      0,
      cropped.width,
      cropped.height,
    );
  return cropped;
}

function prepareArtwork(image, mode, color, threshold, edgeSoftness) {
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  // Preserve existing alpha and discard only the fully transparent padding.
  if (mode === 'transparent') return cropToAlphaBounds(canvas);

  const target = hexToRgb(color);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = imageData.data;
  const feather = mode === 'color' ? 28 : 18;

  for (let i = 0; i < pixels.length; i += 4) {
    if (mode === 'color') {
      const dr = pixels[i] - target.r;
      const dg = pixels[i + 1] - target.g;
      const db = pixels[i + 2] - target.b;
      const distance = Math.sqrt(dr * dr + dg * dg + db * db);

      if (distance <= threshold) {
        pixels[i + 3] = 0;
      } else if (distance < threshold + feather) {
        pixels[i + 3] = Math.round(
          pixels[i + 3] * ((distance - threshold) / feather),
        );
      }
      continue;
    }

    // Rec. 709 luminance separates light/dark artwork more naturally than RGB average.
    const luminance =
      pixels[i] * 0.2126 + pixels[i + 1] * 0.7152 + pixels[i + 2] * 0.0722;

    if (mode === 'light') {
      if (luminance >= threshold) {
        pixels[i + 3] = 0;
      } else if (luminance > threshold - feather) {
        pixels[i + 3] = Math.round(
          pixels[i + 3] * ((threshold - luminance) / feather),
        );
      }
    } else if (mode === 'dark') {
      if (luminance <= threshold) {
        pixels[i + 3] = 0;
      } else if (luminance < threshold + feather) {
        pixels[i + 3] = Math.round(
          pixels[i + 3] * ((luminance - threshold) / feather),
        );
      }
    }
  }

  ctx.putImageData(imageData, 0, 0);

  if (edgeSoftness <= 0) return canvas;

  // Blur only the processed alpha mask, then apply it back to the sharp RGB.
  const softMask = document.createElement('canvas');
  softMask.width = canvas.width;
  softMask.height = canvas.height;
  const softMaskCtx = softMask.getContext('2d');
  softMaskCtx.filter = `blur(${edgeSoftness}px)`;
  softMaskCtx.drawImage(canvas, 0, 0);

  const softened = document.createElement('canvas');
  softened.width = canvas.width;
  softened.height = canvas.height;
  const softenedCtx = softened.getContext('2d');
  softenedCtx.drawImage(canvas, 0, 0);
  softenedCtx.globalCompositeOperation = 'destination-in';
  softenedCtx.drawImage(softMask, 0, 0);
  return softened;
}

export default function BannerCreator({ onSuccess }) {
  const shellRef = useRef(null);
  const previewRef = useRef(null);
  const guideCanvasRef = useRef(null);
  const mobilePreviewEndRef = useRef(null);
  const manaGemGuideRef = useRef(null);
  const iconInputRef = useRef(null);
  const finishedInputRef = useRef(null);
  const baseImagesRef = useRef({});
  const rawIconRef = useRef(null);
  const processedIconRef = useRef(null);
  const uploadedArtworkRef = useRef(null);
  const selectedEmblemImageRef = useRef(null);
  const finishedImageRef = useRef(null);
  const dragRef = useRef(null);
  const bevelCacheRef = useRef({ key: '', effect: null });
  const iconFitScaleRef = useRef(1);

  const { user } = useUser();
  // RANK-1 BANNER CREATOR ACCESS GATE — remove this check to expose all tabs to everyone.
  const hasFullCreatorAccess = Boolean(user && Number(user.rank) === 1);

  const [route, setRoute] = useState(() =>
    hasFullCreatorAccess ? 'create' : 'finished',
  );
  const [emblemQuery, setEmblemQuery] = useState('');
  const [emblemFilter, setEmblemFilter] = useState(0);
  const [emblems, setEmblems] = useState([]);
  const [emblemsLoading, setEmblemsLoading] = useState(false);
  const [emblemBrowserError, setEmblemBrowserError] = useState('');
  const [visibleEmblems, setVisibleEmblems] = useState(48);
  const [selectedEmblem, setSelectedEmblem] = useState(null);
  const [emblemPickerOpen, setEmblemPickerOpen] = useState(true);
  const [baseId, setBaseId] = useState(BANNER_BASES[0].id);
  const [basesReady, setBasesReady] = useState(false);
  const [manaGemGuideReady, setManaGemGuideReady] = useState(false);
  const [iconVersion, setIconVersion] = useState(0);
  const [finishedVersion, setFinishedVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showFullMobilePreview, setShowFullMobilePreview] = useState(false);

  const [iconX, setIconX] = useState(BANNER_BASES[0].iconX);
  const [iconY, setIconY] = useState(BANNER_BASES[0].iconY);
  // Relative size: 1 is the configured alpha-bound fit, 2 is 200%.
  const [iconScale, setIconScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [iconStyle, setIconStyle] = useState('gold');
  const [sourceDetail, setSourceDetail] = useState(0);
  const [surfaceTexture, setSurfaceTexture] = useState(0.34);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1.08);
  const [saturation, setSaturation] = useState(1);
  const [depth, setDepth] = useState(4);
  const [bevelTechnique, setBevelTechnique] = useState('smooth');
  const [bevelDirection, setBevelDirection] = useState('up');
  const [bevelDepth, setBevelDepth] = useState(1000);
  const [bevelSize, setBevelSize] = useState(9.5);
  const [bevelSoftness, setBevelSoftness] = useState(3);
  const [bevelAltitude, setBevelAltitude] = useState(30);
  const [bevelHighlightOpacity, setBevelHighlightOpacity] = useState(0.48);
  const [bevelShadowOpacity, setBevelShadowOpacity] = useState(0.40);
  const [shadow, setShadow] = useState(1);
  const [shine, setShine] = useState(0);
  const [lightAngle, setLightAngle] = useState(120);

  const [clothHue, setClothHue] = useState(0);
  const [clothSaturation, setClothSaturation] = useState(1);
  const [clothDarkness, setClothDarkness] = useState(0);

  const [backgroundMode, setBackgroundMode] = useState('transparent');
  const [removeColor, setRemoveColor] = useState('#ffffff');
  const [removalThreshold, setRemovalThreshold] = useState(225);
  const [edgeSoftness, setEdgeSoftness] = useState(0.7);

  const [bannerName, setBannerName] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    // RANK-1 BANNER CREATOR ACCESS GATE — non-rank-1 users may only use Upload.
    if (!hasFullCreatorAccess && route !== 'finished') setRoute('finished');
  }, [hasFullCreatorAccess, route]);

  useEffect(() => {
    const shell = shellRef.current;
    const endSentinel = mobilePreviewEndRef.current;
    if (!shell || !endSentinel || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setShowFullMobilePreview(entry.isIntersecting),
      { root: shell, threshold: 0.01 },
    );

    observer.observe(endSentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      BANNER_BASES.map(async (base) => {
        const [image, isolated] = await Promise.all([
          imageFromUrl(base.src),
          imageFromUrl(base.isolatedSrc),
        ]);
        return [base.id, { image, isolated }];
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        baseImagesRef.current = Object.fromEntries(entries);
        setBasesReady(true);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchEmblems = useCallback(async (query = '', filter = emblemFilter) => {
    setEmblemsLoading(true);
    setEmblemBrowserError('');
    setVisibleEmblems(48);
    try {
      const response = await fetch(
        `/ajax/customhs.php?customtype=1&type=${filter}&search=${encodeURIComponent(query)}`,
      );
      if (!response.ok) throw new Error('Could not load custom emblems.');
      const data = await response.json();
      setEmblems(Array.isArray(data.emblems) ? data.emblems : []);
    } catch (loadError) {
      setEmblems([]);
      setEmblemBrowserError(loadError.message);
    } finally {
      setEmblemsLoading(false);
    }
  }, [emblemFilter]);

  useEffect(() => {
    if (route === 'emblem' && emblems.length === 0 && !emblemsLoading) {
      fetchEmblems('', emblemFilter);
    }
    // Only fetch automatically when entering this route; searches are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  useEffect(() => {
    let cancelled = false;
    imageFromUrl(MANA_GEM_GUIDE_SRC)
      .then((image) => {
        if (cancelled) return;
        manaGemGuideRef.current = image;
        setManaGemGuideReady(true);
      })
      .catch(() => {
        // The banner editor still works if the optional placement guide is absent.
        if (!cancelled) setManaGemGuideReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const canvas = guideCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, RENDER_WIDTH, RENDER_HEIGHT);
    if (!manaGemGuideRef.current) return;
    ctx.setTransform(RENDER_SCALE, 0, 0, RENDER_SCALE, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      manaGemGuideRef.current,
      MANA_GEM_GUIDE_X,
      MANA_GEM_GUIDE_Y,
    );
  }, [manaGemGuideReady]);

  useEffect(() => {
    if (!rawIconRef.current) return;
    try {
      processedIconRef.current = prepareArtwork(
        rawIconRef.current,
        backgroundMode,
        removeColor,
        removalThreshold,
        edgeSoftness,
      );
      const base = BANNER_BASES.find((item) => item.id === baseId);
      iconFitScaleRef.current = getFittedIconScale(
        processedIconRef.current,
        base,
      );
      setIconVersion((value) => value + 1);
      setError('');
    } catch {
      setError('Icon filtering could not read this image. Try a local file.');
    }
  }, [backgroundMode, baseId, edgeSoftness, removeColor, removalThreshold]);

  const drawPreview = useCallback(() => {
    const canvas = previewRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, RENDER_WIDTH, RENDER_HEIGHT);
    ctx.setTransform(RENDER_SCALE, 0, 0, RENDER_SCALE, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (route === 'finished') {
      if (finishedImageRef.current) {
        ctx.drawImage(finishedImageRef.current, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      }
      return;
    }

    const baseBundle = baseImagesRef.current[baseId];
    if (!baseBundle) return;
    const { image: base, isolated } = baseBundle;

    ctx.drawImage(base, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);

    /*
     * All cloth treatment happens on the supplied isolated layer. Masking it
     * again at the end guarantees that color/shine never reaches the metal rim.
     */
    const clothLayer = document.createElement('canvas');
    clothLayer.width = RENDER_WIDTH;
    clothLayer.height = RENDER_HEIGHT;
    const clothCtx = clothLayer.getContext('2d');
    clothCtx.setTransform(RENDER_SCALE, 0, 0, RENDER_SCALE, 0, 0);
    clothCtx.imageSmoothingEnabled = true;
    clothCtx.imageSmoothingQuality = 'high';
    clothCtx.filter = `hue-rotate(${clothHue}deg) saturate(${clothSaturation})`;
    clothCtx.drawImage(isolated, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    clothCtx.filter = 'none';
    if (clothDarkness > 0) {
      clothCtx.globalCompositeOperation = 'multiply';
      clothCtx.globalAlpha = clothDarkness;
      clothCtx.fillStyle = '#000000';
      clothCtx.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    }
    clothCtx.globalAlpha = 1;
    clothCtx.globalCompositeOperation = 'destination-in';
    clothCtx.drawImage(isolated, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    ctx.drawImage(clothLayer, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);

    const icon = processedIconRef.current;
    if (!icon) return;

    const styled = styleIcon(
      icon,
      iconStyle,
      brightness,
      contrast,
      saturation,
      sourceDetail,
    );
    const dark = makeTintedAlpha(styled, 'rgba(28,16,11,0.92)');
    const useGeneratedMaterial = iconStyle !== 'original';
    const fittedIconScale = iconFitScaleRef.current * iconScale;
    const width = styled.width * fittedIconScale;
    const height = styled.height * fittedIconScale;

    /*
     * Render every artwork effect to its own full-size layer, then use the
     * isolated cloth alpha as the exact clipping mask.
     */
    const artworkLayer = document.createElement('canvas');
    artworkLayer.width = RENDER_WIDTH;
    artworkLayer.height = RENDER_HEIGHT;
    const artworkCtx = artworkLayer.getContext('2d');
    artworkCtx.setTransform(RENDER_SCALE, 0, 0, RENDER_SCALE, 0, 0);
    artworkCtx.imageSmoothingEnabled = true;
    artworkCtx.imageSmoothingQuality = 'high';
    artworkCtx.save();
    artworkCtx.translate(iconX, iconY);
    artworkCtx.rotate((rotation * Math.PI) / 180);

    // Soft cast shadow is available for both Image and generated materials.
    if (shadow > 0) {
      artworkCtx.save();
      artworkCtx.globalAlpha = shadow;
      artworkCtx.filter = `blur(${3 * RENDER_SCALE}px)`;
      artworkCtx.drawImage(dark, -width / 2 + 3, -height / 2 + 5, width, height);
      artworkCtx.restore();
    }

    if (useGeneratedMaterial) {
      // A small repeated offset creates the raised/extruded edge.
      for (let step = depth; step >= 1; step -= 1) {
        artworkCtx.globalAlpha = 0.7 + (step / Math.max(depth, 1)) * 0.2;
        artworkCtx.drawImage(
          dark,
          -width / 2 + step * 0.7,
          -height / 2 + step * 0.85,
          width,
          height,
        );
      }

    }
    artworkCtx.globalAlpha = 1;
    artworkCtx.drawImage(styled, -width / 2, -height / 2, width, height);

    // Inner Bevel is applied only to generated gold/silver/white materials.
    if (useGeneratedMaterial && bevelSize > 0 && bevelDepth > 0) {
      const bevelKey = [
        iconVersion,
        bevelTechnique,
        bevelDirection,
        bevelDepth,
        bevelSize,
        bevelSoftness,
        lightAngle,
        bevelAltitude,
        bevelHighlightOpacity,
        bevelShadowOpacity,
        surfaceTexture,
      ].join('|');
      let bevelEffect = bevelCacheRef.current.effect;
      if (!bevelEffect || bevelCacheRef.current.key !== bevelKey) {
        bevelEffect = makeBevelEffect(styled, {
          technique: bevelTechnique,
          direction: bevelDirection,
          depth: bevelDepth,
          size: bevelSize,
          softness: bevelSoftness,
          angle: lightAngle,
          altitude: bevelAltitude,
          highlightOpacity: bevelHighlightOpacity,
          shadowOpacity: bevelShadowOpacity,
          texture: surfaceTexture,
        });
        bevelCacheRef.current = { key: bevelKey, effect: bevelEffect };
      }
      artworkCtx.globalCompositeOperation = 'multiply';
      artworkCtx.globalAlpha = 1;
      artworkCtx.drawImage(
        bevelEffect.shadow,
        -width / 2,
        -height / 2,
        width,
        height,
      );
      artworkCtx.globalCompositeOperation = 'screen';
      artworkCtx.drawImage(
        bevelEffect.highlight,
        -width / 2,
        -height / 2,
        width,
        height,
      );
      artworkCtx.globalCompositeOperation = 'source-over';
      artworkCtx.globalAlpha = 1;
    }

    // Directional highlight clipped by the icon alpha.
    if (useGeneratedMaterial && shine > 0) {
      const shineLayer = document.createElement('canvas');
      shineLayer.width = styled.width;
      shineLayer.height = styled.height;
      const shineCtx = shineLayer.getContext('2d');
      shineCtx.drawImage(styled, 0, 0);
      shineCtx.globalCompositeOperation = 'source-in';

      const radians = (lightAngle * Math.PI) / 180;
      const cx = styled.width / 2;
      const cy = styled.height / 2;
      const radius = Math.max(styled.width, styled.height) * 0.72;
      const gradient = shineCtx.createLinearGradient(
        cx - Math.cos(radians) * radius,
        cy - Math.sin(radians) * radius,
        cx + Math.cos(radians) * radius,
        cy + Math.sin(radians) * radius,
      );
      gradient.addColorStop(0, 'rgba(255,255,255,0)');
      gradient.addColorStop(0.38, 'rgba(255,255,255,0)');
      gradient.addColorStop(0.5, 'rgba(255,255,255,0.9)');
      gradient.addColorStop(0.62, 'rgba(255,255,255,0)');
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      shineCtx.fillStyle = gradient;
      shineCtx.fillRect(0, 0, styled.width, styled.height);

      artworkCtx.globalCompositeOperation = 'screen';
      artworkCtx.globalAlpha = shine;
      artworkCtx.drawImage(shineLayer, -width / 2, -height / 2, width, height);
    }
    artworkCtx.restore();
    artworkCtx.globalAlpha = 1;
    artworkCtx.globalCompositeOperation = 'destination-in';
    artworkCtx.drawImage(isolated, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    ctx.drawImage(artworkLayer, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  }, [
    baseId,
    basesReady,
    bevelAltitude,
    bevelDepth,
    bevelDirection,
    bevelHighlightOpacity,
    bevelSize,
    bevelShadowOpacity,
    bevelSoftness,
    bevelTechnique,
    brightness,
    clothDarkness,
    clothHue,
    clothSaturation,
    contrast,
    depth,
    finishedVersion,
    iconScale,
    iconStyle,
    iconVersion,
    iconX,
    iconY,
    lightAngle,
    rotation,
    route,
    saturation,
    shadow,
    shine,
    sourceDetail,
    surfaceTexture,
  ]);

  useEffect(() => {
    drawPreview();
  }, [drawPreview]);

  const installArtworkImage = (
    image,
    mode = backgroundMode,
    { resetPlacement = true, sampleBackground = true } = {},
  ) => {
    rawIconRef.current = image;

    let sampledColor = removeColor;
    if (sampleBackground) {
      const sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = 1;
      sampleCanvas.height = 1;
      const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
      sampleCtx.drawImage(image, 0, 0, 1, 1);
      const sample = sampleCtx.getImageData(0, 0, 1, 1).data;
      sampledColor = rgbToHex(sample[0], sample[1], sample[2]);
      setRemoveColor(sampledColor);
    }

    processedIconRef.current = prepareArtwork(
      image,
      mode,
      sampledColor,
      removalThreshold,
      edgeSoftness,
    );
    const base = BANNER_BASES.find((item) => item.id === baseId);
    iconFitScaleRef.current = getFittedIconScale(
      processedIconRef.current,
      base,
    );
    if (resetPlacement) {
      setIconX(base?.iconX ?? 78);
      setIconY(base?.iconY ?? 238);
      setIconScale(1);
      setRotation(0);
    }
    setIconVersion((value) => value + 1);
  };

  const handleIconFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const image = await imageFromFile(file);
      uploadedArtworkRef.current = image;
      setIconStyle('original');
      installArtworkImage(image);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSelectEmblem = async (item) => {
    setBusy(true);
    setError('');
    try {
      const image = await imageFromUrl(
        `/assets/emblems/${item.emblemId}.png`,
      );
      const replacingExistingEmblem = Boolean(selectedEmblemImageRef.current);
      const mode = replacingExistingEmblem ? backgroundMode : 'transparent';
      if (!replacingExistingEmblem) setBackgroundMode('transparent');
      selectedEmblemImageRef.current = image;
      installArtworkImage(image, mode, {
        resetPlacement: !replacingExistingEmblem,
        sampleBackground: false,
      });
      setSelectedEmblem(item);
      setEmblemPickerOpen(false);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setBusy(false);
    }
  };

  const handleEmblemFilter = (filter) => {
    setEmblemFilter(filter);
    setEmblemQuery('');
    fetchEmblems('', filter);
  };

  const handleEmblemBrowserScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setVisibleEmblems((count) => Math.min(count + 48, emblems.length));
    }
  };

  const handleFinishedFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const image = await imageFromFile(file);
      if (image.width !== OUTPUT_WIDTH || image.height !== OUTPUT_HEIGHT) {
        throw new Error(
          `Finished banners must be exactly ${OUTPUT_WIDTH}×${OUTPUT_HEIGHT}px. ` +
            `This file is ${image.width}×${image.height}px.`,
        );
      }
      finishedImageRef.current = image;
      setFinishedVersion((value) => value + 1);
    } catch (loadError) {
      finishedImageRef.current = null;
      setFinishedVersion((value) => value + 1);
      setError(loadError.message);
    } finally {
      setBusy(false);
    }
  };

  const setRouteAndClearError = (nextRoute) => {
    // RANK-1 BANNER CREATOR ACCESS GATE — block restricted routes as well as hiding them.
    if (!hasFullCreatorAccess && nextRoute !== 'finished') return;
    setRoute(nextRoute);
    setError('');
    if (nextRoute === 'create' && uploadedArtworkRef.current) {
      installArtworkImage(uploadedArtworkRef.current, backgroundMode);
    } else if (nextRoute === 'create') {
      rawIconRef.current = null;
      processedIconRef.current = null;
      setIconVersion((value) => value + 1);
    }
    if (nextRoute === 'emblem') {
      setIconStyle('gold');
      setEmblemPickerOpen(!selectedEmblemImageRef.current);
      if (selectedEmblemImageRef.current) {
        installArtworkImage(selectedEmblemImageRef.current, backgroundMode, {
          resetPlacement: false,
          sampleBackground: false,
        });
      } else {
        rawIconRef.current = null;
        processedIconRef.current = null;
        setIconVersion((value) => value + 1);
      }
    }
  };

  const handlePointerDown = (event) => {
    if (route === 'finished' || !processedIconRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      iconX,
      iconY,
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = OUTPUT_WIDTH / rect.width;
    const scaleY = OUTPUT_HEIGHT / rect.height;
    setIconX(
      clamp(
        dragRef.current.iconX + (event.clientX - dragRef.current.pointerX) * scaleX,
        12,
        OUTPUT_WIDTH - 12,
      ),
    );
    setIconY(
      clamp(
        dragRef.current.iconY + (event.clientY - dragRef.current.pointerY) * scaleY,
        20,
        OUTPUT_HEIGHT - 20,
      ),
    );
  };

  const handlePointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
  };

  const downloadPng = async () => {
    if (!previewRef.current) return;
    try {
      const outputCanvas = makeOutputCanvas(
        previewRef.current,
        route === 'finished' ? finishedImageRef.current : null,
      );
      const blob = await canvasToBlob(outputCanvas);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bannerName.trim() || 'custom-banner'}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const submitBanner = async () => {
    const hasImage =
      route === 'finished' ? finishedImageRef.current : processedIconRef.current;
    if (!hasImage || !bannerName.trim()) {
      setError('Choose an image and give the banner a name first.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      // Always submit the flattened preview, even for a JPG/WebP source.
      const outputCanvas = makeOutputCanvas(
        previewRef.current,
        route === 'finished' ? finishedImageRef.current : null,
      );
      const blob = await canvasToBlob(outputCanvas);
      const formData = new FormData();
      formData.append('banner', blob, 'banner.png');
      formData.append('emblemName', bannerName.trim());
      formData.append('emblemPublic', isPublic ? 'true' : 'false');

      const response = await fetch('/ajax/banner_recieve.php', {
        method: 'POST',
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || (payload.status && payload.status !== 'success')) {
        throw new Error(payload.code || payload.error || 'Banner upload failed.');
      }
      onSuccess?.();
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
    }
  };

  const hasOutput =
    route === 'finished' ? Boolean(finishedImageRef.current) : Boolean(processedIconRef.current);

  return (
    <>
      <div ref={shellRef} className="banner-creator-shell row g-0 h-100 overflow-auto">
      <div className="col-12 col-lg-7 p-3 p-md-4 border-end border-secondary">
        <div
          className="banner-creator-route-tabs d-grid gap-2 mb-4"
          style={{
            gridTemplateColumns: `repeat(${hasFullCreatorAccess ? 3 : 1}, minmax(0, 1fr))`,
          }}
        >
          {/* RANK-1 BANNER CREATOR ACCESS GATE — remove this wrapper to show these tabs to everyone. */}
          {hasFullCreatorAccess && (
            <>
              <button
                type="button"
                className={`btn flex-fill ${route === 'create' ? 'btn-warning' : 'btn-outline-secondary'}`}
                onClick={() => setRouteAndClearError('create')}
              >
                <FaMagic className="me-2" />
                Create
              </button>
              <button
                type="button"
                className={`btn flex-fill ${route === 'emblem' ? 'btn-warning' : 'btn-outline-secondary'}`}
                onClick={() => setRouteAndClearError('emblem')}
              >
                <FaImage className="me-2" />
                Emblems
              </button>
            </>
          )}
          <button
            type="button"
            className={`btn flex-fill ${route === 'finished' ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => setRouteAndClearError('finished')}
          >
            <FaImage className="me-2" />
            Upload
          </button>
        </div>

        {route === 'finished' ? (
          <section>
            <label className="form-label text-warning fw-bold">1. Download templates</label>
            <p className="small text-muted mb-2">
              Edit one of the example banners without changing its dimensions or
              surrounding transparency.
            </p>
            <a
              className="btn btn-outline-warning w-100 mb-4"
              href={BANNER_TEMPLATE_ZIP}
              download
            >
              <FaDownload className="me-2" />
              Download banner examples (.zip)
            </a>

            <label className="form-label text-warning fw-bold">2. Finished banner file</label>
            <input
              ref={finishedInputRef}
              type="file"
              className="form-control bg-dark text-white border-secondary"
              accept="image/*"
              onChange={handleFinishedFile}
            />
            <p className="small text-muted mt-2">
              Image must be a PNG of exactly 171×349 pixels.
            </p>
          </section>
        ) : (
          <div className="d-flex flex-column gap-3">
            <section>
              <label className="form-label text-warning fw-bold">1. Banner base</label>
              <div className="d-flex flex-wrap gap-2">
                {BANNER_BASES.map((base) => (
                  <button
                    type="button"
                    key={base.id}
                    className={`btn btn-sm ${
                      baseId === base.id ? 'btn-warning' : 'btn-outline-secondary'
                    }`}
                    onClick={() => {
                      setBaseId(base.id);
                      setIconX(base.iconX);
                      setIconY(base.iconY);
                      iconFitScaleRef.current = getFittedIconScale(
                        processedIconRef.current,
                        base,
                      );
                    }}
                  >
                    {base.label}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <label className="form-label text-warning fw-bold">2. Icon</label>
              {route === 'create' ? (
                <input
                  ref={iconInputRef}
                  type="file"
                  className="form-control bg-dark text-white border-secondary"
                  accept="image/*"
                  onChange={handleIconFile}
                />
              ) : selectedEmblem && !emblemPickerOpen ? (
                <div className="d-flex align-items-center gap-3 rounded border border-secondary bg-black bg-opacity-25 p-2">
                  <img
                    src={`/assets/emblems/preview/${selectedEmblem.emblemId}.webp`}
                    alt=""
                    width="58"
                    height="58"
                    style={{ objectFit: 'contain' }}
                  />
                  <div className="min-w-0 flex-grow-1">
                    <div className="text-truncate fw-bold">
                      {selectedEmblem.emblemName}
                    </div>
                    <div className="small text-success">Selected custom emblem</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => setEmblemPickerOpen(true)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="rounded border border-secondary bg-black bg-opacity-25 p-2">
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {[
                      [0, 'All'],
                      [1, 'My Emblems'],
                      [2, 'Favorited'],
                    ].map(([filter, label]) => (
                      <button
                        type="button"
                        key={filter}
                        className={`btn btn-sm ${
                          emblemFilter === filter
                            ? 'btn-warning'
                            : 'btn-outline-secondary'
                        }`}
                        onClick={() => handleEmblemFilter(filter)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <form
                    className="input-group input-group-sm mb-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      fetchEmblems(emblemQuery, emblemFilter);
                    }}
                  >
                    <input
                      type="search"
                      className="form-control bg-dark text-white border-secondary"
                      placeholder="Search custom emblems…"
                      value={emblemQuery}
                      onChange={(event) => setEmblemQuery(event.target.value)}
                    />
                    <button className="btn btn-outline-warning" type="submit">
                      <FaSearch />
                    </button>
                  </form>

                  {emblemsLoading ? (
                    <div className="text-center text-muted py-4">
                      <FaSpinner className="fa-spin text-warning me-2" />
                      Loading emblems…
                    </div>
                  ) : emblemBrowserError ? (
                    <div className="text-danger small py-2">{emblemBrowserError}</div>
                  ) : emblems.length === 0 ? (
                    <div className="text-muted small text-center py-4">
                      No custom emblems found.
                    </div>
                  ) : (
                    <div
                      className="banner-emblem-grid"
                      onScroll={handleEmblemBrowserScroll}
                    >
                      {emblems.slice(0, visibleEmblems).map((item, index) => (
                        <button
                          type="button"
                          className="banner-emblem-option"
                          key={`${item.emblemId}-${index}`}
                          title={item.emblemName}
                          onClick={() => handleSelectEmblem(item)}
                        >
                          <img
                            loading="lazy"
                            src={`/assets/emblems/preview/${item.emblemId}.webp`}
                            alt=""
                          />
                          <span>{item.emblemName}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedEmblem && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={() => setEmblemPickerOpen(false)}
                    >
                      Keep current selection
                    </button>
                  )}
                </div>
              )}
              <div className="row g-2 mt-1">
                <div className="col-sm-7">
                  <label className="form-label small mb-1">Style</label>
                  <select
                    className="form-select form-select-sm bg-dark text-white border-secondary"
                    value={iconStyle}
                    onChange={(event) => setIconStyle(event.target.value)}
                  >
                    {ICON_STYLES.filter(
                      (style) => route !== 'emblem' || style.id !== 'original',
                    ).map((style) => (
                      <option key={style.id} value={style.id}>
                        {style.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-5">
                  <label className="form-label small mb-1">Rotation: {rotation}°</label>
                  <input
                    type="range"
                    className="form-range"
                    min="-180"
                    max="180"
                    value={rotation}
                    onChange={(event) => setRotation(Number(event.target.value))}
                  />
                </div>
              </div>
            </section>

            <section className="border border-secondary rounded p-3 bg-black bg-opacity-25">
              <label className="form-label small mb-1">Icon background</label>
              <select
                className="form-select form-select-sm bg-dark text-white border-secondary"
                value={backgroundMode}
                onChange={(event) => {
                  const mode = event.target.value;
                  setBackgroundMode(mode);
                  if (mode === 'light') setRemovalThreshold(225);
                  if (mode === 'dark') setRemovalThreshold(30);
                  if (mode === 'color') setRemovalThreshold(32);
                }}
              >
                <option value="transparent">Use existing transparency</option>
                <option value="light">Remove light pixels</option>
                <option value="dark">Remove dark pixels</option>
                <option value="color">Remove selected color</option>
              </select>
              {backgroundMode !== 'transparent' && (
                <div className="row g-2 align-items-end">
                  {backgroundMode === 'color' && (
                    <div className="col-auto">
                      <label className="form-label small mb-1">Color</label>
                      <input
                        type="color"
                        className="form-control form-control-color bg-dark border-secondary"
                        value={removeColor}
                        onChange={(event) => setRemoveColor(event.target.value)}
                      />
                    </div>
                  )}
                  <div className="col">
                    <label className="form-label small mb-1">
                      {backgroundMode === 'color' ? 'Tolerance' : 'Threshold'}:{' '}
                      {removalThreshold}
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max={backgroundMode === 'color' ? 160 : 255}
                      value={removalThreshold}
                      onChange={(event) => setRemovalThreshold(Number(event.target.value))}
                    />
                  </div>
                  <Range
                    label="Edge softness"
                    min="0"
                    max="2.5"
                    step="0.05"
                    value={edgeSoftness}
                    onChange={setEdgeSoftness}
                  />
                </div>
              )}
            </section>

            <section className="border border-secondary rounded p-3">
              <div className="row g-3">
                <Range label="Icon size" min="0.01" max="2" step="0.002" value={iconScale} onChange={setIconScale} />
                <Range label="Brightness" min="0.5" max="1.8" step="0.01" value={brightness} onChange={setBrightness} />
                <Range label="Shadow" min="0" max="1" step="0.01" value={shadow} onChange={setShadow} />
                {iconStyle !== 'original' && (
                  <>
                    <Range label="Extrusion depth" min="0" max="10" step="1" value={depth} onChange={setDepth} />
                    <Range label="Source detail" min="0" max="1" step="0.01" value={sourceDetail} onChange={setSourceDetail} />
                    <Range label="Shadow mottle" min="0" max="1" step="0.01" value={surfaceTexture} onChange={setSurfaceTexture} />
                    <Range label="Icon shine" min="0" max="1" step="0.01" value={shine} onChange={setShine} />
                    <Range label="Contrast" min="0.5" max="1.8" step="0.01" value={contrast} onChange={setContrast} />
                    <Range label="Saturation" min="0" max="2" step="0.01" value={saturation} onChange={setSaturation} />
                  </>
                )}
              </div>
            </section>

            {iconStyle !== 'original' && (
            <section className="border border-secondary rounded p-3">
              <div className="small text-warning fw-bold mb-2">Inner Bevel</div>
              <div className="row g-3">
                <div className="col-12 col-sm-6">
                  <label className="form-label small mb-1">Technique</label>
                  <select
                    className="form-select form-select-sm bg-dark text-white border-secondary"
                    value={bevelTechnique}
                    onChange={(event) => setBevelTechnique(event.target.value)}
                  >
                    <option value="smooth">Smooth</option>
                    <option value="chiselSoft">Chisel Soft</option>
                    <option value="chiselHard">Chisel Hard</option>
                  </select>
                </div>
                <div className="col-12 col-sm-6">
                  <label className="form-label small mb-1">Direction</label>
                  <select
                    className="form-select form-select-sm bg-dark text-white border-secondary"
                    value={bevelDirection}
                    onChange={(event) => setBevelDirection(event.target.value)}
                  >
                    <option value="up">Up</option>
                    <option value="down">Down</option>
                  </select>
                </div>
                <Range label="Depth" min="0" max="1000" step="10" value={bevelDepth} onChange={setBevelDepth} suffix="%" />
                <Range label="Size" min="0" max="30" step="0.5" value={bevelSize} onChange={setBevelSize} suffix="px" />
                <Range label="Soften" min="0" max="20" step="0.25" value={bevelSoftness} onChange={setBevelSoftness} suffix="px" />
                <Range label="Light angle" min="0" max="360" step="1" value={lightAngle} onChange={setLightAngle} suffix="°" />
                <Range label="Light altitude" min="0" max="90" step="1" value={bevelAltitude} onChange={setBevelAltitude} suffix="°" />
                <Range label="Highlight opacity" min="0" max="1" step="0.01" value={bevelHighlightOpacity} onChange={setBevelHighlightOpacity} />
                <Range label="Shadow opacity" min="0" max="1" step="0.01" value={bevelShadowOpacity} onChange={setBevelShadowOpacity} />
              </div>
            </section>
            )}

            <section className="border border-secondary rounded p-3">
              <div className="small text-warning fw-bold mb-2">Banner cloth</div>
              <div className="row g-3">
                <Range label="Hue shift" min="-180" max="180" step="1" value={clothHue} onChange={setClothHue} suffix="°" />
                <Range label="Cloth saturation" min="0" max="2" step="0.01" value={clothSaturation} onChange={setClothSaturation} />
                <Range label="Cloth darkness" min="0" max="0.7" step="0.01" value={clothDarkness} onChange={setClothDarkness} />
              </div>
            </section>
          </div>
        )}

        <section className="mt-4">
          <label className="form-label text-warning fw-bold">3. Finalize</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control bg-dark text-white border-secondary"
              placeholder="Name your banner"
              value={bannerName}
              onChange={(event) => setBannerName(event.target.value)}
            />
            <label className="input-group-text bg-dark text-white border-secondary">
              <input
                type="checkbox"
                className="form-check-input mt-0 me-2"
                checked={isPublic}
                onChange={(event) => setIsPublic(event.target.checked)}
              />
              Public
            </label>
          </div>
          {error && (
            <div className="alert alert-danger py-2 small mt-3 mb-0">
              {error}
            </div>
          )}
          <div
            ref={mobilePreviewEndRef}
            className="banner-creator-mobile-end-sentinel"
            aria-hidden="true"
          />
        </section>
      </div>

      <div
        className={`banner-creator-preview-column col-12 col-lg-5 p-3 p-md-4 d-flex flex-column align-items-center ${
          showFullMobilePreview ? 'banner-creator-mobile-full-preview' : ''
        }`}
      >
        <div
          className="banner-creator-preview-stage position-relative d-flex justify-content-center align-items-center rounded border border-secondary bg-black bg-opacity-50 p-3"
          style={{
            backgroundImage:
              'linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)',
            backgroundSize: '18px 18px',
            backgroundPosition: '0 0,0 9px,9px -9px,-9px 0',
          }}
        >
          <div className="banner-creator-canvas-stack">
            <canvas
              ref={previewRef}
              className="banner-creator-preview-canvas"
              width={RENDER_WIDTH}
              height={RENDER_HEIGHT}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                width: OUTPUT_WIDTH,
                height: OUTPUT_HEIGHT,
                maxWidth: '100%',
                objectFit: 'contain',
                cursor: route !== 'finished' && hasOutput ? 'move' : 'default',
                touchAction: 'none',
                filter: 'drop-shadow(0 10px 14px rgba(0,0,0,.55))',
              }}
            />
            <canvas
              ref={guideCanvasRef}
              className="banner-creator-guide-canvas"
              width={RENDER_WIDTH}
              height={RENDER_HEIGHT}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <div className="banner-creator-upload-row col-12 col-lg-7">
        <div className="d-flex gap-2">
          {/* Final PNG download is intentionally hidden; keep downloadPng for easy restoration. */}
          {false && (
            <button
              type="button"
              className="btn btn-outline-light"
              disabled={!hasOutput || busy}
              onClick={downloadPng}
              title="Download the exact PNG that will be uploaded"
            >
              <FaDownload />
            </button>
          )}
          <button
            type="button"
            className="btn btn-warning fw-bold flex-grow-1"
            disabled={!hasOutput || !bannerName.trim() || busy}
            onClick={submitBanner}
          >
            {busy ? (
              <FaSpinner className="fa-spin me-2" />
            ) : (
              <FaUpload className="me-2" />
            )}
            {busy ? 'Working…' : 'Upload banner'}
          </button>
        </div>
      </div>
      </div>
    </>
  );
}

function Range({ label, value, onChange, suffix = '', ...props }) {
  return (
    <div className="col-12 col-sm-6">
      <label className="form-label small mb-0">
        {label}: {Number(value).toFixed(Number(props.step) < 1 ? 2 : 0)}
        {suffix}
      </label>
      <input
        type="range"
        className="form-range"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        {...props}
      />
    </div>
  );
}
